import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity, MessageCircle, UserPlus, UserMinus, Eye, EyeOff,
  ArrowRightLeft, Sparkles, CheckCircle2,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * QENVA · Activity Timeline.
 *
 * Renders the merged event stream (state changes + comments + assignments
 * + watcher changes) for one alert. Pure display component.
 */
const EVENT_DEF = {
  state_change:      { icon: ArrowRightLeft, color: "text-qenva-blue", bg: "bg-qenva-blue/10",       label_fr: "Changement d'état" },
  assign:            { icon: UserPlus,       color: "text-emerald-600", bg: "bg-emerald-50",         label_fr: "Assignation" },
  unassign:          { icon: UserMinus,      color: "text-zinc-500",    bg: "bg-zinc-100",           label_fr: "Désassignation" },
  comment:           { icon: MessageCircle,  color: "text-qenva-blue",  bg: "bg-qenva-blue/10",      label_fr: "Commentaire" },
  comment_edit:      { icon: MessageCircle,  color: "text-zinc-500",    bg: "bg-zinc-100",           label_fr: "Commentaire modifié" },
  comment_delete:    { icon: MessageCircle,  color: "text-rose-500",    bg: "bg-rose-50",            label_fr: "Commentaire supprimé" },
  watcher_add:       { icon: Eye,            color: "text-emerald-600", bg: "bg-emerald-50",         label_fr: "Watcher ajouté" },
  watcher_remove:    { icon: EyeOff,         color: "text-zinc-500",    bg: "bg-zinc-100",           label_fr: "Watcher retiré" },
  impact_update:     { icon: Sparkles,       color: "text-amber-600",   bg: "bg-amber-50",           label_fr: "Impact mis à jour" },
};

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.round(diff / 86400)} j`;
    return d.toLocaleDateString();
  } catch (_) {
    return iso?.slice(0, 10) || "—";
  }
}

export default function ActivityTimeline({ alertId, testId = "activity-timeline" }) {
  const { t } = useTranslation("alerts");
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!alertId) return;
    let cancelled = false;
    Promise.all([
      api.get(`/alm/alerts/${alertId}/activity`).catch(() => ({ data: { items: [] } })),
      api.get("/alm/members").catch(() => ({ data: { items: [] } })),
    ]).then(([ev, mem]) => {
      if (cancelled) return;
      setEvents(ev.data?.items || []);
      setMembers(mem.data?.items || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [alertId]);

  const memberById = {};
  for (const m of members) memberById[m.user_id] = m;

  const describePayload = (e) => {
    const p = e.payload || {};
    const actor = memberById[e.actor_user_id]?.name || memberById[e.actor_user_id]?.email || t("activity.unknown_user");
    const target = (uid) => memberById[uid]?.name || memberById[uid]?.email || uid || "—";
    switch (e.event_type) {
      case "state_change":
        if (!p.from_state && !p.to_state) return actor;
        return `${actor} · ${p.from_state || "—"} → ${p.to_state || "—"}`;
      case "assign":
        return `${actor} → ${target(p.to_user_id)}`;
      case "unassign":
        return `${actor} (depuis ${target(p.from_user_id)})`;
      case "watcher_add":
        return `${actor} → ${target(p.user_id)}`;
      case "watcher_remove":
        return `${actor} ↛ ${target(p.user_id)}`;
      case "comment":
        return `${actor} : « ${p.excerpt || "…"} »`;
      case "comment_edit":
      case "comment_delete":
        return `${actor}`;
      case "impact_update":
        return `${actor} · ${p.new_amount || "—"}`;
      default:
        return actor;
    }
  };

  return (
    <section data-testid={testId}>
      <header className="flex items-center gap-2 mb-3">
        <Activity size={14} className="text-qenva-blue" />
        <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-qenva-blue">
          {t("activity.title")}
        </p>
        <span className="text-[11.5px] text-zinc-400 tabular-nums">({events.length})</span>
      </header>

      {loading ? (
        <p className="text-[12px] text-zinc-400" data-testid={`${testId}-loading`}>{t("common.loading")}</p>
      ) : events.length === 0 ? (
        <p className="text-[12px] text-zinc-400 italic" data-testid={`${testId}-empty`}>{t("activity.empty")}</p>
      ) : (
        <ol className="space-y-2.5 relative pl-3" data-testid={`${testId}-list`}>
          <span className="absolute left-[7px] top-1 bottom-1 w-px bg-zinc-200" aria-hidden />
          {events.map((e, i) => {
            const def = EVENT_DEF[e.event_type] || EVENT_DEF.state_change;
            const Icon = def.icon;
            return (
              <li key={e.id || i} className="relative flex items-start gap-2.5" data-testid={`${testId}-event-${i}`}>
                <span className={`absolute -left-[2px] top-1 w-[16px] h-[16px] rounded-full ring-2 ring-white grid place-items-center ${def.bg} ${def.color}`}>
                  <Icon size={9} strokeWidth={2.25} />
                </span>
                <div className="ml-5 flex-1 min-w-0">
                  <p className="text-[12.5px] text-zinc-900 truncate" title={describePayload(e)}>
                    <span className="font-semibold text-qenva-navy">{def.label_fr}</span>
                    <span className="text-zinc-500"> · {describePayload(e)}</span>
                  </p>
                  <p className="text-[10.5px] text-zinc-400 mt-0.5">{relativeTime(e.created_at)}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
